"use client";

import { useRef, useState, useTransition } from "react";
import { useCommentsContextOptional } from "@/components/comments-list-client";
import { createComment } from "@/lib/actions/comments";
import { useReplyStore } from "@/lib/state/reply-store";

const MAX_LENGTH = 1500;

export const REPLY_INPUT_ID = "post-reply-input";

/** What a thread hangs off: a post, a submission, a story or a Drive item. */
export type CommentTarget =
	| { kind: "post"; postId: number }
	| { kind: "submission"; submissionId: number }
	| { kind: "editorial"; slug: string }
	| { kind: "upload"; uploadId: number };

export function commentTargetIds(target: CommentTarget) {
	return {
		postId: target.kind === "post" ? target.postId : undefined,
		submissionId:
			target.kind === "submission" ? target.submissionId : undefined,
		editorialSlug: target.kind === "editorial" ? target.slug : undefined,
		sceneUploadId: target.kind === "upload" ? target.uploadId : undefined,
	};
}

function escapeHtml(text: string) {
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

/** Plain text from the textarea as paragraphs, the shape comment bodies use. */
function toHtml(text: string) {
	return text
		.trim()
		.split(/\n{2,}/)
		.map(
			(paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`,
		)
		.join("");
}

/**
 * The one reply form at the end of a thread. "Reply to …" on any reply points
 * it at that reply; otherwise it replies to the post, track or story.
 */
export function EditorialReplyForm({
	target,
	replyToLabel,
}: {
	target: CommentTarget;
	/** Who or what a top-level reply answers, e.g. "@danny_d". */
	replyToLabel: string;
}) {
	const ids = commentTargetIds(target);
	const commentsContext = useCommentsContextOptional();
	const parentId = useReplyStore((state) => state.activeCommentId);
	const parentAuthor = useReplyStore((state) => state.activeAuthor);
	const clearReply = useReplyStore((state) => state.clearReply);
	const [text, setText] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, startTransition] = useTransition();
	const submittingRef = useRef(false);

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (submittingRef.current) return;
		if (!text.trim()) {
			setError("Write something first.");
			return;
		}
		const body = toHtml(text);
		const replyTo = parentId;
		setError(null);
		submittingRef.current = true;

		startTransition(async () => {
			commentsContext?.addOptimisticComment({
				body,
				...ids,
				parentId: replyTo ?? null,
			});
			try {
				const result = await createComment({
					post_id: ids.postId ?? null,
					submission_id: ids.submissionId ?? null,
					editorial_slug: ids.editorialSlug ?? null,
					scene_upload_id: ids.sceneUploadId ?? null,
					parent_id: replyTo ?? undefined,
					body,
				});
				if (result.success) {
					setText("");
					clearReply();
					const data = (
						result as {
							success: true;
							data: Parameters<
								NonNullable<typeof commentsContext>["addConfirmComment"]
							>[0];
						}
					).data;
					if (data && commentsContext) commentsContext.addConfirmComment(data);
				} else {
					setError(result.error || "Couldn’t post your reply.");
				}
			} catch (caught) {
				setError(
					caught instanceof Error
						? caught.message
						: "Couldn’t post your reply.",
				);
			} finally {
				submittingRef.current = false;
			}
		});
	}

	const label =
		parentId !== null && parentAuthor
			? `Replying to ${parentAuthor}`
			: `Reply to ${replyToLabel}`;

	return (
		<form className="ed-reply-form" onSubmit={submit}>
			<div className="ed-row ed-row--between">
				<label htmlFor={REPLY_INPUT_ID} className="ed-ui ed-secondary">
					{label}
				</label>
				{parentId !== null ? (
					<button
						type="button"
						className="ed-button-text ed-ui ed-secondary"
						onClick={clearReply}
						disabled={isSubmitting}
					>
						Cancel
					</button>
				) : null}
			</div>
			<textarea
				id={REPLY_INPUT_ID}
				className="ed-reply-input"
				rows={3}
				maxLength={MAX_LENGTH}
				placeholder="Add to the conversation…"
				value={text}
				onChange={(event) => setText(event.target.value)}
				disabled={isSubmitting}
			/>
			<div className="ed-row ed-row--between ed-ui">
				<output aria-live="polite" className="ed-secondary">
					{error ?? ""}
				</output>
				<button
					type="submit"
					className="ed-button-text ed-ui ed-link"
					style={{ minHeight: 32 }}
					disabled={isSubmitting}
				>
					{isSubmitting ? "Posting…" : "Reply"}
				</button>
			</div>
		</form>
	);
}
